import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ExternalLink, Store, Ticket, MessageCircle, Loader2, ArrowLeft, Send,
} from "lucide-react";
import { VoteControl } from "@/components/public/VoteControl";
import { useAuth } from "@/context/AuthContext";
import { fetchPublicDeal, fetchComments, addComment } from "@/lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `vor ${Math.max(1, Math.floor(diff / 60))} Min.`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
  return `vor ${Math.floor(diff / 86400)} Tg.`;
};

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchPublicDeal(id)
      .then(setDeal)
      .catch(() => toast.error("Deal nicht gefunden"))
      .finally(() => setLoading(false));
    fetchComments(id).then((d) => setComments(d.comments || [])).catch(() => {});
  }, [id]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const c = await addComment(id, text.trim());
      setComments((prev) => [c, ...prev]);
      setText("");
      toast.success("Kommentar gepostet");
    } catch (err) {
      toast.error("Kommentar fehlgeschlagen");
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (!deal) {
    return <div className="text-center py-32 text-slate-500">Deal nicht gefunden. <Link to="/" className="text-[#A259FF]">Zurück</Link></div>;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6" data-testid="deal-detail-view">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4 font-worksans" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Zurück
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <VoteControl dealId={deal.id} temperature={deal.temperature} size="lg" />
              <span className="text-xs text-slate-400 font-worksans">{timeAgo(deal.created_at)}</span>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 overflow-hidden mb-5 flex items-center justify-center p-4" style={{minHeight: "280px"}}>
              <img src={deal.image_url || "/wizard-icon.png"} alt={deal.product_name} className="max-h-72 w-auto object-contain mx-auto" />
            </div>
            <h1 className="font-outfit font-black text-2xl sm:text-3xl tracking-tight text-slate-900">{deal.product_name}</h1>

            {/* Preishinweis */}
            <div className="mt-3 flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-slate-500 text-xs">
              Preis beim Posten des Deals. Amazon-Preise können sich ändern – immer auf Amazon prüfen.
            </div>

            {deal.price_expired && (
              <div className="mt-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-amber-700 text-sm font-medium" data-testid="price-expired-banner">
                ⚠ Dieser Deal ist möglicherweise abgelaufen – jetzt auf Amazon prüfen.
              </div>
            )}
            <div className="flex items-center flex-wrap gap-3 mt-3">
              <span className="font-outfit font-black text-2xl text-[#FF3B30]">{deal.deal_price.toFixed(2)} €</span>
              <span className="text-base text-slate-400 line-through font-worksans">{deal.original_price.toFixed(2)} €</span>
              {deal.price_expired ? (
                <span className="text-sm font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5">Preis evtl. gestiegen</span>
              ) : (
                <span className="text-sm font-bold text-emerald-600 bg-emerald-50 rounded-md px-2 py-0.5">-{deal.discount_percent}%</span>
              )}
            </div>
            {deal.description && (
              <div className="text-slate-700 font-worksans mt-4 leading-relaxed space-y-3">
                {deal.description
                  .replace(/\*\*([^*]+)\*\*/g, '$1')
                  .replace(/#{1,3}\s*/g, '')
                  .replace(/✅\s*/g, '• ')
                  .replace(/➡️\s*/g, '')
                  .replace(/\*\s*/g, '')
                  .replace(/(Was bekommst du\??:?)\s*/g, '')
                  .replace(/(Highlights?:?)\s*/g, '')
                  .replace(/(Zum Preis:?)\s*/g, '')
                  .replace(/(Versand & Hinweise?:?)\s*/g, '')
                  .split(/\n\n+/)
                  .filter(p => p.trim().length > 10)
                  .map((para, i) => (
                    <p key={i} className="text-sm leading-relaxed text-slate-700">{para.trim()}</p>
                  ))}
              </div>
            )}
            {deal.voucher_code && (
              <div className="mt-4 inline-flex items-center gap-2 bg-[#A259FF]/10 text-[#8928E2] rounded-lg px-3 py-2 font-semibold">
                <Ticket className="h-4 w-4" /> Gutscheincode: <span className="font-mono">{deal.voucher_code}</span>
              </div>
            )}
          </div>

          {/* Comments */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6" data-testid="comments-section">
            <h2 className="font-outfit font-bold text-lg text-slate-900 mb-4 inline-flex items-center gap-2">
              <MessageCircle className="h-5 w-5" /> Kommentare ({comments.length})
            </h2>

            {user ? (
              <form onSubmit={submitComment} className="flex gap-2 mb-6" data-testid="comment-form">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Was hältst du von diesem Deal?"
                  className="flex-1 bg-slate-100 rounded-full px-4 py-2.5 text-sm font-worksans outline-none focus:ring-2 focus:ring-[#A259FF]/40"
                  data-testid="comment-input"
                />
                <button type="submit" disabled={posting} className="ai-gradient-bg text-white rounded-full px-4 py-2.5 font-bold inline-flex items-center gap-1.5 active:scale-95 transition-all" data-testid="comment-submit">
                  {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>
            ) : (
              <div className="bg-slate-50 rounded-xl p-4 text-center mb-6 font-worksans text-sm text-slate-600">
                <Link to="/login" className="text-[#A259FF] font-semibold">Melde dich an</Link>, um mitzudiskutieren.
              </div>
            )}

            <div className="space-y-4">
              {comments.length === 0 ? (
                <p className="text-sm text-slate-400 font-worksans text-center py-4">Noch keine Kommentare. Sei der Erste!</p>
              ) : (
                comments.map((c) => (
                  <div key={c.id} className="flex gap-3" data-testid="comment-item">
                    <div className="h-9 w-9 rounded-full ai-gradient-bg text-white flex items-center justify-center font-outfit font-bold text-sm shrink-0">
                      {c.user_name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-900">{c.user_name}</span>
                        <span className="text-xs text-slate-400">{timeAgo(c.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-700 font-worksans mt-0.5">{c.text}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </motion.div>

        {/* Sidebar */}
        <motion.aside initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="lg:sticky lg:top-32 h-fit">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <div>
              <p className="text-xs text-slate-400 font-worksans uppercase tracking-wide">Preis</p>
              <p className="font-outfit font-black text-3xl text-slate-900">{deal.deal_price.toFixed(2)} €</p>
              <p className="text-sm text-slate-400 line-through font-worksans">statt {deal.original_price.toFixed(2)} €</p>
            </div>
            <a
              href={deal.affiliate_url || deal.product_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full ai-gradient-bg text-white font-outfit font-bold rounded-xl px-6 py-3.5 hover:opacity-90 active:scale-95 transition-all shadow-sm inline-flex items-center justify-center gap-2"
              data-testid="zum-deal-cta"
            >
              Zum Deal <ExternalLink className="h-4 w-4" />
            </a>
            <div className="flex items-center gap-2 text-sm text-slate-500 font-worksans pt-2 border-t border-slate-100">
              <Store className="h-4 w-4" /> Bei {deal.source}
            </div>
            {deal.submitter_name && (
              <p className="text-xs text-slate-400 font-worksans">Eingereicht von {deal.submitter_name}</p>
            )}
          </div>
        </motion.aside>
      </div>
    </div>
  );
}
